/**
 * Lap Processing Stage
 *
 * Builds Lap objects for each detected lap window from the synchronized
 * session telemetry.
 */

import {
  PipelineStage,
  PipelineContext,
  StageResult,
} from '../ProcessingPipeline'
import { Lap } from '../../../domain/lap/Lap'
import { LapMetadata } from '../../../domain/lap/LapMetadata'
import { buildLap } from '../../../domain/lap/buildLap'

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export class LapProcessingStage implements PipelineStage {
  name = 'Lap Processing'

  async execute(context: PipelineContext): Promise<StageResult<void>> {
    const warnings: string[] = []

    try {
      const { lapBounds, synchronizedTelemetry, rawTelemetry, dto, track } =
        context

      if (!lapBounds || lapBounds.length === 0) {
        return {
          success: false,
          error: 'No lap bounds available',
        }
      }

      if (!synchronizedTelemetry) {
        return {
          success: false,
          error: 'No synchronized telemetry available',
        }
      }

      if (!rawTelemetry) {
        return {
          success: false,
          error: 'No raw telemetry available',
        }
      }

      const device = rawTelemetry.device
        ? {
            name: rawTelemetry.device.deviceName,
            version: rawTelemetry.device.deviceVersion,
          }
        : undefined

      const laps: Lap[] = []

      for (let i = 0; i < lapBounds.length; i++) {
        const bounds = lapBounds[i]
        const lapNumber = i + 1

        const lapDto = context.lapDtos?.find(
          (candidate) =>
            Math.abs(
              new Date(candidate.startTime).getTime() -
                bounds.startTime.getTime()
            ) < 1000
        )

        const metadata: LapMetadata = {
          lapId: lapDto?.id ?? generateId(),
          lapNumber,
          driverName: 'Unknown',
          trackSessionId: dto.id,
          trackId: track.id,
        }

        const lap = buildLap({
          metadata,
          window: bounds,
          sessionTelemetry: synchronizedTelemetry,
          sessionRawTelemetry: rawTelemetry,
          device,
        })

        if (!lap) {
          warnings.push(`Lap ${lapNumber} has no telemetry data`)
          continue
        }

        laps.push(lap)
      }

      context.laps = laps

      if (laps.length === 0) {
        return {
          success: false,
          error: 'No laps could be created',
        }
      }

      return {
        success: true,
        warnings: warnings.length > 0 ? warnings : undefined,
      }
    } catch (error) {
      return {
        success: false,
        error: `Lap processing failed: ${error instanceof Error ? error.message : String(error)}`,
      }
    }
  }
}
